import type { VerdictSportData } from "@/app/_components/conditions/VerdictBlock";
import { gapForecastDayWords, type GapForecastInfo } from "@/lib/conditions/gap-forecast-display";
import type { SportBadge } from "@/lib/conditions/unified-types";

// The verdict during the nightly gap (lib/conditions/gap-forecast.ts): each sport whose badge
// carries the previous run's forecast for today shows that number under "Forecast issued 21:56
// CEST" in place of "Today's score", with the forecast day's caution (amber reasons, safety not
// assessed, the avalanche bulletin) and no readings, explanation, evidence or tactics from the
// earlier day. Only the verdict reads this: the rest of the page keeps its stale state, so no
// strip, chart, planner or visualiser takes the forecast for an observed or current score.

export function verdictsWithGapForecast(
  perSport: Readonly<Record<string, VerdictSportData>>,
  badges: readonly SportBadge[],
  gapForecast: GapForecastInfo | null | undefined,
): Record<string, VerdictSportData> {
  const out: Record<string, VerdictSportData> = { ...perSport };
  if (!gapForecast) return out;
  const forDay = `${gapForecast.issuedLabel} for ${gapForecastDayWords(gapForecast.date)}`;
  for (const badge of badges) {
    if (!badge.forecast) continue;
    const key = badge.sport === "swimming" && !out.swimming ? "swim" : badge.sport;
    const base = out[key];
    if (!base) continue;
    const { explanation: _explanation, activeFit: _activeFit, evidenceSlot: _evidenceSlot, ...kept } = base;
    out[key] = badge.score === null
      ? { ...kept, score: null, label: badge.label, note: null, tacticalLine: null, metrics: [], unavailableReason: `${badge.label ?? "Score unavailable"}. ${forDay}.` }
      : {
          ...kept,
          score: badge.score,
          label: badge.label,
          note: `${forDay}.${badge.forecastCaution ? ` ${badge.forecastCaution}` : ""} The next update replaces it.`,
          tacticalLine: null,
          metrics: [],
          forecastHeading: gapForecast.issuedLabel,
        };
  }
  return out;
}

/** Whether the active sport's badge carries the gap forecast (the status notice speaks for it only). */
export function activeBadgeIsGapForecast(badges: readonly SportBadge[], activeSport: string): boolean {
  const key = activeSport === "swim" ? "swimming" : activeSport;
  return badges.some((badge) => (badge.sport === key || badge.sport === activeSport) && badge.forecast === true);
}
